import { useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";

export interface UserProfile {
  id: string;
  name: string | null;
  lastname: string | null;
  email: string | null;
  fullName: string; // Computed property
}

export const useUserProfiles = (userIds: string[]) => {
  const [profiles, setProfiles] = useState<Record<string, UserProfile>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      // Filter out empty IDs and IDs we already have cached
      const idsToFetch = userIds.filter((id) => id && !profiles[id]);
      if (idsToFetch.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, lastname, email")
          .in("id", idsToFetch);

        if (error) {
          throw error;
        }

        const newProfiles: Record<string, UserProfile> = { ...profiles };

        data.forEach((profile) => {
          const fullName =
            [profile.name, profile.lastname].filter(Boolean).join(" ") ||
            profile.email ||
            "Usuario";

          newProfiles[profile.id] = {
            ...profile,
            fullName,
          };
        });

        setProfiles(newProfiles);
      } catch (err) {
        console.error("Error fetching user profiles:", err);
        setError("No se pudieron cargar los perfiles de usuarios");
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [userIds]);

  const getUserName = (userId: string | null | undefined): string => {
    if (!userId) return "Sistema";
    return profiles[userId]?.fullName || `${userId.substring(0, 6)}...`;
  };

  return { profiles, loading, error, getUserName };
};
